<template>
  <div class="meeting-invite">
    <header class="meeting-invite__header">
      <img
        v-if="meeting && meeting.company_logo"
        :src="meeting.company_logo"
        :alt="meeting.company_name || 'Logo da empresa'"
      />
      <img v-else src="@/assets/images/logo/logo.png" alt="Recrutei" />
    </header>

    <main class="meeting-invite__main">
      <ReLoading v-if="isLoading" />

      <section v-else-if="notFound" class="meeting-invite__card meeting-invite__card--center">
        <h1>Link de reunião inválido</h1>
        <p class="meeting-invite__muted">
          Confira se o link está completo, ou acesse a área do candidato para ver suas reuniões.
        </p>
        <a class="meeting-invite__link" :href="platformUrl">Acessar a área do candidato</a>
      </section>

      <section v-else-if="loadError" class="meeting-invite__card meeting-invite__card--center">
        <h1>Não foi possível carregar a reunião</h1>
        <p class="meeting-invite__muted">Tente de novo em alguns instantes.</p>
        <button class="meeting-invite__btn meeting-invite__btn--primary" @click="load">
          Tentar de novo
        </button>
      </section>

      <section v-else class="meeting-invite__card">
        <span class="meeting-invite__badge" :class="`meeting-invite__badge--${meeting.state}`">
          {{ badge }}
        </span>
        <p v-if="meeting.talent_first_name" class="meeting-invite__hello">
          Oi, {{ meeting.talent_first_name }}!
        </p>
        <h1>{{ headline }}</h1>
        <p v-if="subline" class="meeting-invite__muted">{{ subline }}</p>

        <div class="meeting-invite__when">
          <div class="meeting-invite__date">
            <span class="meeting-invite__day">{{ day }}</span>
            <span class="meeting-invite__month">{{ month }}</span>
          </div>
          <div>
            <p class="meeting-invite__weekday">{{ weekday }}</p>
            <p class="meeting-invite__time">{{ timeRange }}</p>
          </div>
        </div>

        <ul class="meeting-invite__details">
          <li v-if="meeting.vacancy_title">
            <span>Vaga</span><strong>{{ meeting.vacancy_title }}</strong>
          </li>
          <li v-if="withWhom">
            <span>Com</span><strong>{{ withWhom }}</strong>
          </li>
          <li v-if="meeting.type">
            <span>Tipo</span><strong>{{ meeting.type }}</strong>
          </li>
          <li v-if="meeting.place">
            <span>Local</span><strong>{{ meeting.place }}</strong>
          </li>
        </ul>

        <div class="meeting-invite__actions">
          <button
            v-if="meeting.state === 'pending'"
            class="meeting-invite__btn meeting-invite__btn--primary"
            :disabled="isConfirming"
            @click="confirm"
          >
            {{ isConfirming ? "Confirmando..." : "Confirmar presença" }}
          </button>
          <a
            v-if="joinUrl"
            class="meeting-invite__btn"
            :class="joinIsTeams ? 'meeting-invite__btn--teams' : 'meeting-invite__btn--meet'"
            :href="joinUrl"
            target="_blank"
            rel="noopener"
          >
            {{ joinLabel }}
          </a>
        </div>
        <p v-if="confirmError" class="meeting-invite__error">{{ confirmError }}</p>
        <p v-if="justConfirmed" class="meeting-invite__muted">
          Enviamos a confirmação para o seu e-mail{{ joinUrl ? ", com o link da reunião" : "" }}.
        </p>

        <a class="meeting-invite__link" :href="platformUrl">Acessar a área do candidato</a>
      </section>
    </main>
  </div>
</template>

<script>
import api from "@/services/api";
import ReLoading from "@/components/re-loading/ReLoading.vue";
import { meetingJoinUrl, meetingJoinLabel } from "@/utils/meetingJoin";

// Página pública do convite de reunião (link do e-mail, sem login). Abrir a
// página nunca confirma: scanners de e-mail visitam o link. Só o clique em
// "Confirmar presença" faz o POST.
export default {
  name: "MeetingInvite",
  components: { ReLoading },
  data: () => ({
    meeting: null,
    isLoading: true,
    notFound: false,
    loadError: false,
    isConfirming: false,
    confirmError: null,
    justConfirmed: false,
  }),
  computed: {
    token() {
      // Mesmo formato que a API assina: "<id>.<32 hex>". Qualquer outra coisa nem vai à API.
      const token = String(this.$route.params.token || "");
      return /^\d{1,12}\.[a-f0-9]{32}$/.test(token) ? token : null;
    },
    platformUrl() {
      const slug = /^[a-z0-9-]+$/i;
      const label = [
        this.meeting && this.meeting.company_label,
        this.$route.params.company_label,
      ].find((value) => value && slug.test(value)) || "recrutei-empregos";
      return `/${label}/login`;
    },
    start() {
      if (!this.meeting || !this.meeting.date) return null;
      const [y, m, d] = this.meeting.date.split("-").map(Number);
      return new Date(y, m - 1, d);
    },
    day() {
      return this.start ? String(this.start.getDate()).padStart(2, "0") : "";
    },
    month() {
      return this.start
        ? this.start.toLocaleDateString("pt-BR", { month: "short" }).replace(".", "").toUpperCase()
        : "";
    },
    weekday() {
      if (!this.start) return "";
      const text = this.start.toLocaleDateString("pt-BR", {
        weekday: "long",
        day: "numeric",
        month: "long",
      });
      return text.charAt(0).toUpperCase() + text.slice(1);
    },
    timeRange() {
      if (!this.meeting || !this.meeting.time) return "";
      const minutes = this.meeting.duration_minutes;
      if (!minutes) return this.meeting.time;
      const [h, m] = this.meeting.time.split(":").map(Number);
      const total = h * 60 + m + minutes;
      const end = `${String(Math.floor(total / 60) % 24).padStart(2, "0")}:${String(total % 60).padStart(2, "0")}`;
      return `${this.meeting.time} às ${end}`;
    },
    withWhom() {
      if (!this.meeting) return null;
      const parts = [this.meeting.manager_name, this.meeting.company_name].filter(Boolean);
      return parts.length ? parts.join(" · ") : null;
    },
    joinUrl() {
      if (!this.meeting || this.meeting.state === "past") return null;
      return meetingJoinUrl(this.meeting);
    },
    joinLabel() {
      return meetingJoinLabel(this.meeting);
    },
    joinIsTeams() {
      return this.meeting && this.meeting.join_provider === "teams";
    },
    badge() {
      return {
        pending: "Aguardando sua confirmação",
        confirmed: "Presença confirmada",
        rejected: "Reunião recusada",
        counter: "Novo horário sugerido",
        cancelled: "Reunião cancelada",
        past: "Reunião encerrada",
      }[this.meeting.state];
    },
    headline() {
      return {
        pending: "Você foi convidado(a) para uma reunião",
        confirmed: "Sua presença está confirmada",
        rejected: "Você recusou esta reunião",
        counter: "Você sugeriu outro horário",
        cancelled: "Esta reunião foi cancelada",
        past: "Esta reunião já aconteceu",
      }[this.meeting.state];
    },
    subline() {
      return {
        pending: "Confira os detalhes e confirme sua presença.",
        confirmed: this.joinUrl ? "No horário marcado, é só entrar pelo botão abaixo." : "Te esperamos no dia e horário marcados.",
        rejected: "Se mudou de ideia, fale com quem te convidou.",
        counter: "Quem te convidou ainda vai responder a sua sugestão.",
        cancelled: "Quem te convidou cancelou a reunião. Se precisar, fale com a empresa.",
        past: null,
      }[this.meeting.state];
    },
  },
  created() {
    this.load();
  },
  methods: {
    async load() {
      this.isLoading = true;
      this.loadError = false;
      if (!this.token) {
        this.notFound = true;
        this.isLoading = false;
        return;
      }
      try {
        const { data } = await api.get(`api/v1/meeting-invites/${encodeURIComponent(this.token)}`);
        this.meeting = data.data;
      } catch (error) {
        if (error.response && error.response.status === 404) this.notFound = true;
        else this.loadError = true;
      } finally {
        this.isLoading = false;
      }
    },
    async confirm() {
      this.isConfirming = true;
      this.confirmError = null;
      try {
        const { data } = await api.post(`api/v1/meeting-invites/${encodeURIComponent(this.token)}/confirm`);
        this.meeting = data.data;
        this.justConfirmed = !!data.confirmed_now;
      } catch (error) {
        this.confirmError =
          (error.response && error.response.data && error.response.data.message) ||
          "Não foi possível confirmar agora. Tente de novo em alguns instantes.";
      } finally {
        this.isConfirming = false;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.meeting-invite {
  min-height: 100vh;
  background: #f2f4f7;
  color: #101828;

  &__header {
    display: flex;
    align-items: center;
    height: 64px;
    padding: 0 24px;
    background: #fff;
    border-bottom: 1px solid #eaecf0;

    img {
      max-height: 40px;
      max-width: 180px;
      object-fit: contain;
    }
  }

  &__main {
    display: flex;
    justify-content: center;
    padding: 40px 16px 64px;
  }

  &__card {
    width: 100%;
    max-width: 560px;
    padding: 32px;
    background: #fff;
    border: 1px solid #eaecf0;
    border-radius: 16px;
    box-shadow: 0 1px 3px rgba(16, 24, 40, 0.06);

    &--center {
      text-align: center;
    }

    h1 {
      margin: 4px 0 8px;
      font-size: 24px;
      font-weight: 700;
      line-height: 32px;
    }
  }

  &__hello {
    margin-top: 16px;
    font-size: 16px;
    color: #475467;
  }

  &__muted {
    font-size: 15px;
    line-height: 22px;
    color: #475467;
  }

  &__badge {
    display: inline-block;
    padding: 4px 12px;
    font-size: 13px;
    font-weight: 600;
    border-radius: 999px;
    background: #fef0c7;
    color: #93370d;

    &--confirmed {
      background: #d1fadf;
      color: #05603a;
    }
    &--rejected,
    &--cancelled {
      background: #fee4e2;
      color: #912018;
    }
    &--past,
    &--counter {
      background: #eaecf0;
      color: #344054;
    }
  }

  &__when {
    display: flex;
    align-items: center;
    gap: 16px;
    margin: 24px 0 8px;
    padding: 16px;
    background: #f9fafb;
    border-radius: 12px;
  }

  &__date {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 64px;
    height: 64px;
    border-radius: 12px;
    background: #fff;
    border: 1px solid #eaecf0;
  }

  &__day {
    font-size: 24px;
    font-weight: 700;
    line-height: 26px;
  }

  &__month {
    font-size: 12px;
    font-weight: 600;
    color: #667085;
  }

  &__weekday {
    font-size: 16px;
    font-weight: 600;
  }

  &__time {
    margin-top: 2px;
    font-size: 15px;
    color: #475467;
  }

  &__details {
    margin: 8px 0 0;
    padding: 0;
    list-style: none;

    li {
      display: flex;
      gap: 12px;
      padding: 12px 0;
      border-bottom: 1px solid #f2f4f7;
      font-size: 15px;

      span {
        flex: 0 0 64px;
        color: #667085;
      }

      strong {
        font-weight: 600;
        word-break: break-word;
      }
    }
  }

  &__actions {
    display: flex;
    flex-wrap: wrap;
    gap: 12px;
    margin-top: 24px;
  }

  &__btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex: 1 1 200px;
    min-height: 48px;
    padding: 0 20px;
    font-size: 16px;
    font-weight: 600;
    color: #fff;
    border: none;
    border-radius: 999px;
    cursor: pointer;
    text-decoration: none;

    &:disabled {
      opacity: 0.6;
      cursor: default;
    }

    &--primary {
      background: rgba(var(--vs-primary), 1);
    }
    &--teams {
      background: #5b5fc7;
    }
    &--meet {
      background: #00897b;
    }
  }

  &__error {
    margin-top: 12px;
    font-size: 14px;
    color: #b42318;
  }

  &__link {
    display: inline-block;
    margin-top: 24px;
    font-size: 14px;
    color: rgba(var(--vs-primary), 1);
  }
}

@media (max-width: 480px) {
  .meeting-invite__card {
    padding: 24px 20px;
  }
}
</style>
